import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { checkoutReturnFields } from "@/lib/tenantPath";
import { checkoutErrorMessage } from "@/lib/checkoutErrors";
import { currentRef } from "../lib/affiliateRef";
// --- crm-module BEGIN C3.04 (marketplace attribution -- write side) ---
import { currentPromotionRef } from "../lib/promotionRef";
// --- crm-module END ---
import { toast } from "sonner";
import { useLanguage, useT, useLocalized } from "@/lib/LanguageContext";
import { useRequireAuth } from "@/components/auth/useRequireAuth";
import ThemedPageLayout from "@/templates/ThemedPageLayout";
import PurchasePanel from "@/components/checkout/PurchasePanel";
import { Check, ArrowRight, Loader2, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

// No default currency. The server fills `currency` on every public catalogue row with
// what the checkout would actually charge; a `"cad"` invented here stamped the retired
// settlement currency on rows the platform now charges in USD.
const money = (cents, currency) => `${(cents / 100).toFixed(0)} ${String(currency || "").toUpperCase()}`.trim();

// Can this row be bought online? BOTH halves are the server's law, restated for a stale
// payload: a positive price (BUG-046 — a zero price is a pricing defect, not a free
// offer) AND `payable_online` not explicitly false (a row held in a currency the platform
// no longer settles; the checkout would refuse it, so the card must not offer it).
// `!== false`, not `=== true`: a payload that predates the flag is judged on price alone.
const purchasable = (p) => p.price_cents > 0 && p.payable_online !== false;

export default function Programmes() {
  const { lang } = useLanguage();
  const t = useT("public");
  const tc = useT("common");
  const loc = useLocalized();
  const { ensureAuthed, authDialog } = useRequireAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  // THE PROGRAMME THE BUYER CHOSE, and the only thing that opens the purchase panel
  // (BUG-049). The promo code and the gift card used to live HERE, above the whole grid,
  // where a code could be accepted with no programme to apply it to and a card had no
  // amount to quote itself against. Both now belong to `PurchasePanel`, which knows
  // which programme is being bought, what it costs with tax, and in which currency.
  const [chosen, setChosen] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/programs");
        setItems(data);
      } finally { setLoading(false); }
    })();
  }, []);

  const buy = async (p, { promoCode = "", giftCardCode = "", marketingOptIn = false } = {}) => {
    // Defense in depth: the buy button never renders for an unpurchasable row (see the
    // grid below), but `buy` refuses it directly too rather than trusting the render path.
    if (!purchasable(p)) return;
    const u = await ensureAuthed();
    if (!u) return;
    setBusy(p.id);
    try {
      const { data } = await api.post("/checkout/program", {
        program_id: p.id,
        origin_url: window.location.origin,
        ...checkoutReturnFields("/merci/programme", "/programmes"),
        // THE BUYER'S LANGUAGE (BUG-106). Without it the checkout fell back to the
        // PRACTICE's locale, so the payment page and the confirmation email named the
        // programme — and addressed the buyer — in the practitioner's language.
        locale: lang,
        // Omitted entirely when the buyer typed nothing: the server treats an absent
        // code as "no promo", and sending "" would be a value it has to interpret.
        ...(promoCode ? { promo_code: promoCode } : {}),
        ...(giftCardCode ? { gift_card_code: giftCardCode } : {}),
        // Attribution (contract §2): the referral code the visitor arrived with, or "".
        // Sent on every capturable checkout so the sale can be traced back to whoever
        // introduced the buyer; the server resolves it and silently ignores an invalid,
        // self-referring or expired one — a checkout never fails on a query parameter the
        // buyer did not choose.
        ref: currentRef(),
        // --- crm-module BEGIN C3.04 (marketplace attribution -- write side) ---
        promotion_id: currentPromotionRef() || undefined,
        // --- crm-module END ---
        // The buyer's answer to the panel's optional marketing box (false untouched);
        // recorded by the server only once the sale is paid.
        marketing_opt_in: marketingOptIn === true,
      });
      window.location.href = data.url;
    } catch (e) {
      // `detail` is no longer a string on this path: a refusal arrives as
      // {code, message} and a 422 as a LIST. Rendering it raw printed
      // "[object Object]" to the buyer, or an English sentence to an FR/ES one.
      // THE APP'S OWN SURFACE, never `alert()`. A native dialog blocks the renderer
      // until it is dismissed (the row underneath stays stuck in its busy state), cannot
      // be themed inside a practitioner's storefront, and can be suppressed by the
      // browser after a repeat — at which point the NEXT failure really is invisible.
      // The message itself is unchanged: `checkoutErrorMessage` maps the server's stable
      // CODE to a translated sentence, because `extractApiError` alone would surface the
      // backend's ENGLISH diagnostic to an FR/ES buyer.
      toast.error(checkoutErrorMessage(e, t, t("programs.toast.error")));
    } finally { setBusy(null); }
  };

  return (
    <ThemedPageLayout>
      <section className="pt-32 pb-12 max-w-7xl mx-auto px-6">
        <div className="max-w-3xl">
          <span className="eyebrow">{t("programs.eyebrow")}</span>
          <h1 className="font-display text-5xl md:text-6xl mt-3 leading-tight">{t("programs.heading")}</h1>
          <p className="mt-4 text-lg text-eh-muted">
            {t("programs.subheading")}
          </p>
        </div>

        <div className="mt-14">
          {loading ? (
            <div className="text-eh-muted flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> {tc("actions.loading")}</div>
          ) : items.length === 0 ? (
            <div className="text-eh-muted text-sm bg-eh-cream border border-eh-border-soft rounded-2xl p-10 text-center">
              {t("programs.empty")}
            </div>
          ) : (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((p, i) => {
                const name = loc(p, "name");
                const tagline = loc(p, "tagline");
                const desc = loc(p, "description");
                const features = (lang === "en" ? (p.features_en?.length ? p.features_en : p.features_fr) : p.features_fr) || [];
                const isFeatured = p.featured;
                // See `purchasable`: a zero/absent price (BUG-046) or a currency the
                // platform cannot settle both render the "cannot be paid online" copy
                // instead of Enrol. The price itself is still shown, as held.
                const unavailable = !purchasable(p);
                return (
                  <motion.div
                    key={p.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: i * 0.06 }}
                    data-testid={`program-${p.id}`}
                    className={`relative rounded-[2rem] overflow-hidden border ${isFeatured ? "bg-eh-ink text-eh-bg border-eh-ink" : "bg-eh-cream text-eh-ink border-eh-border-soft"} flex flex-col`}
                  >
                    {isFeatured && (
                      <div className="absolute top-4 right-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] bg-eh-ochre text-eh-ink px-2.5 py-1 rounded-full">
                        <Sparkles size={10} /> {t("programs.featured")}
                      </div>
                    )}
                    {p.image_url && <img src={p.image_url} alt={name} className="aspect-[5/3] w-full object-cover" />}
                    <div className="p-7 flex-1 flex flex-col">
                      <div className={`text-xs uppercase tracking-[0.18em] ${isFeatured ? "text-eh-ochre" : "text-eh-sage-deep"}`}>{p.duration_label || t("programs.durationFallback")}</div>
                      <h3 className="font-display text-3xl mt-2 leading-tight">{name}</h3>
                      {tagline && <div className={`mt-1 italic font-display text-lg ${isFeatured ? "text-eh-bg/70" : "text-eh-muted"}`}>{tagline}</div>}
                      {desc && <p className={`mt-3 text-sm leading-relaxed ${isFeatured ? "text-eh-bg/80" : "text-eh-muted"}`}>{desc}</p>}
                      {features.length > 0 && (
                        <ul className="mt-5 space-y-2 text-sm">
                          {features.map((f, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <Check size={14} className={`mt-1 shrink-0 ${isFeatured ? "text-eh-ochre" : "text-eh-sage-deep"}`} />
                              <span>{f}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="mt-auto pt-7 flex items-end justify-between gap-3">
                        <div>
                          <div className={`text-xs uppercase tracking-[0.18em] ${isFeatured ? "text-eh-bg/60" : "text-eh-muted"}`}>{t("programs.investment")}</div>
                          <div className="font-display text-3xl mt-0.5">{money(p.price_cents, p.currency)}</div>
                        </div>
                        {unavailable ? (
                          // Reuses the existing money-path refusal copy (checkoutErrors.js
                          // CHECKOUT_ERROR_KEYS.INVALID_PRICE): same meaning — "this price
                          // cannot be paid online" — already translated in all 3 locales.
                          <span
                            className={`text-sm italic ${isFeatured ? "text-eh-bg/60" : "text-eh-muted"}`}
                            data-testid={`program-unavailable-${p.id}`}
                          >
                            {t("checkout.error.invalidPrice")}
                          </span>
                        ) : (
                          <button
                            onClick={() => setChosen(p)}
                            disabled={busy === p.id}
                            className={`btn-primary text-sm shrink-0 ${isFeatured ? "bg-eh-ochre text-eh-ink hover:bg-eh-ochre-deep hover:text-eh-bg" : ""}`}
                            data-testid={`program-buy-${p.id}`}
                          >
                            {busy === p.id ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />} {t("programs.cta")}
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
            </>
          )}
        </div>
      </section>
      {/* The chosen programme, its full cost and the instruments for it (BUG-049).
          Rendered here rather than inside the card so the panel is one dialog per page,
          never one per tile. */}
      {chosen && (
        <PurchasePanel
          name={loc(chosen, "name")}
          priceCents={chosen.price_cents}
          currency={chosen.currency}
          kind="program"
          offeringType="program"
          offeringId={chosen.id}
          payLabel={t("programs.cta")}
          busy={busy === chosen.id}
          onPay={(codes) => buy(chosen, codes)}
          onClose={() => setChosen(null)}
        />
      )}
      {authDialog}
    </ThemedPageLayout>
  );
}
